import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faFileExport } from '@fortawesome/free-solid-svg-icons';

import TitlePage from '../../components/general/TitlePage/TitlePage';
import TitleSectionComponent from '../../components/intern/TitleSectionComponent/TitleSectionComponent';
import FieldWithTooltip from '../../components/Tooltip/FieldWithTooltip';
import Textarea from '../../components/general/Textarea/Textarea';
import Select from '../../components/general/Input/Select/Select';
import ButtonComponent from '../../components/general/Button/Button';
import RadioGroup from '../../components/RadioGroup/RadioGroup';
import CheckBoxContainer from '../../components/general/CheckBox/CheckBoxContainer';
import Input from '../../components/general/Input/Input';

const Briefing = () => {
    const [showModal, setShowModal] = useState(false);

    // Solução para Input Descontrolado: Inicializar tudo com string vazia
    const [formData, setFormData] = useState({
        cuenta: '', advertiser: '', nombre_campana: '', media_buyer: '', fecha_inicio: '', kpis: '', canales: '',
        is_dco: '', codigos_terceros: '', quien_desarrolla: '', dimensiones: '',
        formatos: [], n_conceptos: '', n_versiones: '', req_especiales: '', n_kvs: '',
        tamanos_req: '', estrategia_targeting: '', reglas_dinamicas: '', arbol_link: '',
        use_feed: '', formato_feed: '', origen_data: '', frecuencia_feed: '',
        tiempo_implementacion: '', has_tag_manager: '', tag_manager_existente: '',
        accept_js: '', pasar_variables: '', lista_variables: '', ventana_atribucion: '',
        req_especiales_reporte: '', nomenclatura: '', viewability: '',
        analytics_creativo: '', atribucion_reporte: '', log_files: '',
        procesamiento_log: '', ftp_s3: ''
    });

    // Bloqueio de Scroll quando modal abre
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showModal]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const isFormEmpty = () => {
        return !Object.values(formData).some(value => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'number') return true;
            return false;
        });
    };

    const withDefault = (options) => [{ label: 'Seleccionar...', value: '' }, ...options];
    const yesNoOptions = [{ label: 'Si', value: 'Si' }, { label: 'No', value: 'No' }];

    const stabilizedFormatOptions = React.useMemo(() => [
        { value: 'Standard', label: <FieldWithTooltip label="Standard" tooltip="Formatos display padrão IAB." /> },
        { value: 'Rich', label: <FieldWithTooltip label="Rich" tooltip="Peças interativas avançadas." /> },
        { value: 'Expandible', label: <FieldWithTooltip label="Expandible" tooltip="Banners que expandem." /> },
        { value: 'Rich Media con Video', label: <FieldWithTooltip label="Rich Media con Video" tooltip="Vídeo in-banner." /> },
        { value: 'Vast Dinamico', label: <FieldWithTooltip label="Vast Dinamico" tooltip="Tags VAST dinâmicas." /> }
    ], []);

    const formatOptions = [
        { value: 'Standard', label: 'Standard', tooltip: 'Formatos de display estándar de IAB.' },
        { value: 'Rich', label: 'Rich', tooltip: 'Piezas interactivas avanzadas con funcionalidades ricas.' },
        { value: 'Expandible', label: 'Expandible', tooltip: 'Banners que se expanden al interactuar.' },
        { value: 'Rich Media con Video', label: 'Rich Media con Video', tooltip: 'Piezas interactivas con video in-banner.' },
        { value: 'Vast Dinamico', label: 'Vast Dinamico', tooltip: 'Tags VAST con optimización creativa dinámica.' }
    ];

    const optionsDesarrollo = withDefault([
        { label: 'Equipo Interno', value: 'Equipo Interno' },
        { label: 'Agencia Externa', value: 'Agencia Externa' },
        { label: 'Flashtalking - Innovid', value: 'Flashtalking - Innovid' }
    ]);

    const optionsReglas = withDefault([
        { label: 'Geotargeting', value: 'Geotargeting' },
        { label: 'Contextual', value: 'Contextual' },
        { label: 'Retargeting', value: 'Retargeting' },
        { label: 'Keyword', value: 'Keyword' },
        { label: 'Weather', value: 'Weather' },
        { label: 'Hora/Día de la semana', value: 'Hora/Día de la semana' }
    ]);

    const optionsFormatoFeed = withDefault([
        { label: 'API', value: 'API' },
        { label: 'CSV', value: 'CSV' },
        { label: 'XML', value: 'XML' }
    ]);

    const optionsKPI = withDefault([{ label: 'Performance', value: 'Performance' }, { label: 'Branding', value: 'Branding' }]);
    const optionsCanales = withDefault([{ label: 'Display', value: 'Display' }, { label: 'Mobile', value: 'Mobile' }, { label: 'Video', value: 'Video' }, { label: 'Social', value: 'Social' }]);

    const handleDownload = () => {
        const wb = XLSX.utils.book_new();

        const wsData = [
            ["CAMPAIGN BRIEFING - MIQ & INNOVID"],
            [""],
            ["1. INFORMACIÓN GENERAL"],
            ["Cuenta:", formData.cuenta],
            ["Advertiser:", formData.advertiser],
            ["Campaña:", formData.nombre_campana],
            ["Media Buyer:", formData.media_buyer],
            ["Fecha Inicio:", formData.fecha_inicio],
            ["KPIs:", formData.kpis],
            ["Canales:", formData.canales],
            ["¿Es DCO?:", formData.is_dco],
            ["Códigos de Terceros:", formData.codigos_terceros],
            [""],
            ["2. DESARROLLO CREATIVO"],
            ["¿Quien desarrolla?:", formData.quien_desarrolla],
            ["Dimensiones:", formData.dimensiones],
            ["Formatos:", formData.formatos.join(', ')],
            [""],
            ["3. DCO & ASSETS"],
            ["Conceptos:", formData.n_conceptos],
            ["Versiones:", formData.n_versions],
            ["Req. Especiales:", formData.req_especiales],
            ["KV's:", formData.n_kvs],
            ["Tamaños Requeridos:", formData.tamanos_req],
            ["Estrategia:", formData.estrategia_targeting],
            ["Reglas Dinámicas:", formData.reglas_dinamicas],
            ["Árbol de Decisión:", formData.arbol_link],
            [""],
            ["4. FEED"],
            ["¿Uso de Feed?:", formData.use_feed],
            ["Formato Feed:", formData.formato_feed],
            ["Origen Data:", formData.origen_data],
            ["Frecuencia:", formData.frecuencia_feed],
            [""],
            ["5. PIXEL MANAGER"],
            ["Tiempo Impl.:", formData.tiempo_implementacion],
            ["Tag Manager:", formData.tag_manager_existente],
            ["Acepta JS:", formData.accept_js],
            ["Pasar Variables:", formData.pasar_variables],
            ["Variables:", formData.lista_variables],
            ["Ventana Atribución:", formData.ventana_atribucion],
            [""],
            ["6. REPORTES & LOGS"],
            ["Req. Reporte:", formData.req_especiales_reporte],
            ["Nomenclatura:", formData.nomenclatura],
            ["Viewability:", formData.viewability],
            ["Analytics:", formData.analytics_creativo],
            ["Atribución:", formData.atribucion_reporte],
            ["Log Files:", formData.log_files],
            ["Procesamiento:", formData.procesamiento_log],
            ["FTP/S3:", formData.ftp_s3]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws['!cols'] = [
            { wch: 35 },
            { wch: 60 }
        ];

        XLSX.utils.book_append_sheet(wb, ws, "Briefing_Tecnico");

        const fileName = formData.nombre_campana
            ? `Briefing_${formData.nombre_campana.replace(/\s+/g, '_')}.xlsx`
            : 'CampaignBriefing_FT-Innovid.xlsx';

        XLSX.writeFile(wb, fileName);

        setShowModal(true);
    };

    const inlineStyles = `
    .force-inline .checkbox-container {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        gap: 20px !important;
        background-color: #f9f9f9;
        padding: 10px;
        border-radius: 8px;
        align-items: center;
    }

    /* Estiliza cada item (input + label) */
    .force-inline .checkbox-item {
        display: flex !important;
        align-items: center !important; /* Alinhamento vertical central */
        gap: 8px !important;
        cursor: pointer;
        white-space: nowrap;
    }

    /* Garante que o input e o texto do FieldWithTooltip fiquem na mesma linha */
    .force-inline .checkbox-item input[type="checkbox"] {
        margin: 0 !important;
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    /* Ajuste para o componente FieldWithTooltip dentro do label */
    .force-inline .checkbox-item label {
        display: flex !important;
        align-items: center !important;
        margin-bottom: 0 !important;
    }

    .confirmation-overlay {
        position: fixed;
        top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.7);
        display: flex; align-items: center; justify-content: center;
        z-index: 9999;
        animation: fadeIn 0.3s ease-out;
    }
    .confirmation-popup {
        background: white;
        padding: 40px;
        border-radius: 12px;
        transform: scale(0.7);
        animation: popIn 0.4s forwards cubic-bezier(0.17, 0.89, 0.32, 1.25);
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes popIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
    
    `;

    return (
        <main className="article-page-container">
            <Helmet><title>Campaign Briefing | MIQ & Innovid</title></Helmet>
            <TitlePage title="CAMPAIGN BRIEFING" subtitle="Protocolo técnico oficial de migración y desarrollo." />

            <div className="article-content">
                <form onSubmit={(e) => e.preventDefault()}>

                    <TitleSectionComponent>1. CAMPAIGN BRIEFING</TitleSectionComponent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <FieldWithTooltip label="Cuenta" tooltip="Nombre del cliente en plataforma.">
                            <Input value={formData.cuenta} onChange={e => handleInputChange('cuenta', e.target.value)} />
                        </FieldWithTooltip>
                        <FieldWithTooltip label="Advertiser" tooltip="Marca final del anunciante.">
                            <Input
                                type="text"
                                value={formData.advertiser}
                                onChange={e => handleInputChange('advertiser', e.target.value)}
                            />
                        </FieldWithTooltip>
                        <FieldWithTooltip label="Nombre de Campaña" tooltip="Nombre único de identificación.">
                            <Input
                                type="text"
                                value={formData.nombre_campana}
                                onChange={e => handleInputChange('nombre_campana', e.target.value)}
                            /></FieldWithTooltip>
                        <FieldWithTooltip label="Media Buyer" tooltip="Responsable de la compra."><input type="text" className="form-control" onChange={e => handleInputChange('media_buyer', e.target.value)} /></FieldWithTooltip>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '15px' }}>
                        <FieldWithTooltip label="Fecha de inicio" tooltip="Fecha estimada para el comienzo de la pauta.">
                            <Input
                                type="date"
                                value={formData.fecha_inicio}
                                onChange={e => handleInputChange('fecha_inicio', e.target.value)}
                            /></FieldWithTooltip>
                        <FieldWithTooltip label="KPIs" tooltip="Métrica principal de éxito (ej: CPA, CTR, VTR)."><Select options={optionsKPI} onChange={e => handleInputChange('kpis', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Canales"><Select options={optionsCanales} onChange={e => handleInputChange('canales', e.target.value)} /></FieldWithTooltip>
                    </div>
                    <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <FieldWithTooltip label="¿Es DCO?" tooltip="Indique si la campaña tendrá optimización creativa dinámica."><RadioGroup options={yesNoOptions} value={formData.is_dco} onChange={val => handleInputChange('is_dco', val)} /></FieldWithTooltip>
                        <FieldWithTooltip label="¿Se implementarán códigos de terceros? ¿cuáles?">
                            <Input
                                type="text"
                                value={formData.codigos_terceros}
                                onChange={e => handleInputChange('codigos_terceros', e.target.value)}
                            /></FieldWithTooltip>
                    </div>

                    <TitleSectionComponent>2. DESARROLLO CREATIVO</TitleSectionComponent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <FieldWithTooltip label="¿Quien hará el desarrollo?" tooltip="Responsable de la producción técnica de las piezas."><Select options={optionsDesarrollo} onChange={e => handleInputChange('quien_desarrolla', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Dimensiones (300x250, 300x600, etc)">
                            <Input
                                type="text"
                                value={formData.dimensiones}
                                onChange={e => handleInputChange('dimensiones', e.target.value)}
                            /></FieldWithTooltip>
                    </div>

                    <div className="force-inline">
                        <style>{inlineStyles}</style>

                        <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>Formatos a utilizar:</p>

                        <div className="checkbox-container">
                            <CheckBoxContainer
                                options={stabilizedFormatOptions}
                                initialValues={formData.formatos}
                                onChange={vals => handleInputChange('formatos', vals)}
                            />
                        </div>
                    </div>

                    <TitleSectionComponent>3. DCO & ASSETS</TitleSectionComponent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                        <FieldWithTooltip label="Número de Conceptos" tooltip="Cantidad de variaciones creativas principales"><input type="number" min="0" className="form-control" onChange={e => handleInputChange('n_conceptos', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Número de versiones"><input type="number" min="0" className="form-control" onChange={e => handleInputChange('n_versiones', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Número de KV´S" tooltip="antidad de Key Visuals (artes maestras) suministrados."><input type="number" min="0" className="form-control" onChange={e => handleInputChange('n_kvs', e.target.value)} /></FieldWithTooltip>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <FieldWithTooltip label="Funcionamiento o requerimientos especiales" tooltip="Detalle animaciones complejas o interacciones específicas."><Textarea onChange={e => handleInputChange('req_especiales', e.target.value)} /></FieldWithTooltip></div>

                    <TitleSectionComponent>4. ESTRATEGIA Y TARGETING </TitleSectionComponent>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>
                        <FieldWithTooltip label="Reglas Dinámicas" tooltip="Criterios que activan cada versión del anuncio"><Select options={optionsReglas} onChange={e => handleInputChange('reglas_dinamicas', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Arquitectura del árbol de decisión" tooltip="Insertar link ou indicar anexo."><input type="text" className="form-control" placeholder="Link do diagrama" onChange={e => handleInputChange('arbol_link', e.target.value)} /></FieldWithTooltip>
                    </div>

                    <TitleSectionComponent style={{ marginLeft: '10px' }}>5. FEED </TitleSectionComponent>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <FieldWithTooltip label="¿Se hará uso de Feed?">
                            <RadioGroup
                                options={yesNoOptions}
                                value={formData.use_feed}
                                onChange={val => handleInputChange('use_feed', val)}
                            />
                        </FieldWithTooltip>
                        <FieldWithTooltip label="Formato del Feed"><Select options={optionsFormatoFeed} onChange={e => handleInputChange('formato_feed', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Origen de la data (cliente, feed management, etc)"><input type="text" className="form-control" onChange={e => handleInputChange('origen_data', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="Frecuencia de actualización (min 1 hora)"><input type="text" className="form-control" onChange={e => handleInputChange('frecuencia_feed', e.target.value)} /></FieldWithTooltip>
                    </div>

                    <TitleSectionComponent style={{ marginLeft: '10px' }}>6. PIXEL MANAGER</TitleSectionComponent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                        <FieldWithTooltip label="¿Cuál sería el tiempo necesario para la implementación en el sitio?">
                            <input type="text" className="form-control" onChange={e => handleInputChange('tiempo_implementacion', e.target.value)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿Hay algún sistema de Tag Manager ya implementado? ¿Cuál?">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <RadioGroup
                                    options={yesNoOptions}
                                    value={formData.has_tag_manager}
                                    onChange={val => handleInputChange('has_tag_manager', val)}
                                />
                                {formData.has_tag_manager === 'Si' && (
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Indique cuál sistema"
                                        onChange={e => handleInputChange('tag_manager_existente', e.target.value)}
                                    />
                                )}
                            </div>
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿El sitio acepta código JS?">
                            <input type="text" className="form-control" onChange={e => handleInputChange('accept_js', e.target.value)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿Requieren pasar data a variables?" tooltip="Indique si el píxel debe capturar valores dinámicos">
                            <input type="text" className="form-control" onChange={e => handleInputChange('pasar_variables', e.target.value)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="Enlistar variables">
                            <input type="text" className="form-control" onChange={e => handleInputChange('lista_variables', e.target.value)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿Cual será la ventana de atribución?" tooltip="Periodo de conversión (ej: 30 días post-click, 24h post-view).">
                            <input type="text" className="form-control" onChange={e => handleInputChange('ventana_atribucion', e.target.value)} />
                        </FieldWithTooltip>
                    </div>

                    <TitleSectionComponent>7. REPORTES</TitleSectionComponent>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <FieldWithTooltip label="¿Existen requerimientos especiales?"><input type="text" className="form-control" onChange={e => handleInputChange('req_especiales_reporte', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="¿Existe alguna nomenclatura específica?"><input type="text" className="form-control" onChange={e => handleInputChange('nomenclatura', e.target.value)} /></FieldWithTooltip>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '15px' }}>
                        <FieldWithTooltip label="¿Viewability?">
                            <RadioGroup options={yesNoOptions} value={formData.viewability} onChange={val => handleInputChange('viewability', val)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿Analytics a nivel creativo?">
                            <RadioGroup options={yesNoOptions} value={formData.analytics_creativo} onChange={val => handleInputChange('analytics_creativo', val)} />
                        </FieldWithTooltip>

                        <FieldWithTooltip label="¿Atribución?">
                            <RadioGroup options={yesNoOptions} value={formData.atribucion_reporte} onChange={val => handleInputChange('atribucion_reporte', val)} />
                        </FieldWithTooltip>
                    </div>

                    <TitleSectionComponent>8. LOG FILES</TitleSectionComponent>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '15px' }}>
                        <FieldWithTooltip label="¿Se requieren Log Files?">
                            <RadioGroup
                                options={yesNoOptions}
                                value={formData.log_files}
                                onChange={val => handleInputChange('log_files', val)}
                            />
                        </FieldWithTooltip>
                        <FieldWithTooltip label="¿Quién hará el procesamiento?"><input type="text" className="form-control" onChange={e => handleInputChange('procesamiento_log', e.target.value)} /></FieldWithTooltip>
                        <FieldWithTooltip label="¿Cual es el FTP o S3 disponible?"><input type="text" className="form-control" onChange={e => handleInputChange('ftp_s3', e.target.value)} /></FieldWithTooltip>
                    </div>


                    <div style={{ marginTop: '60px' }}>
                        <ButtonComponent
                            type="button"
                            className={`btn-add ${isFormEmpty() ? 'btn-disable' : ''}`}
                            disabled={isFormEmpty()}
                            onClick={handleDownload}
                            title={isFormEmpty() ? "Debe completar campos para permitir la descarga." : ""}
                            style={{ padding: '15px 50px' }}
                        >
                            <FontAwesomeIcon icon={faFileExport} style={{ marginRight: '10px' }} />
                            FINALIZAR Y EXPORTAR EXCEL
                        </ButtonComponent>
                    </div>
                </form>
            </div>

            {showModal && (
                <div className="confirmation-overlay">
                    <div className="confirmation-popup">
                        <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#1a4a7c" />
                        <h3>Briefing Consolidado</h3>
                        <p>El archivo Excel se ha generado con toda la estructura técnica requerida.</p>
                        <ButtonComponent onClick={() => setShowModal(false)}>Cerrar</ButtonComponent>
                    </div>
                </div>
            )}
        </main>
    );
};

export default Briefing;